/* global gdAlertify */
// Funciones puras (sin JSX) usadas por el módulo CalendarioTurnos.
// Portadas tal cual desde el archivo viejo src/views/CalendarioTurnos.js

import { formatMoney } from "../../utils/utils";

export const getGdAlertify = () => {
    if (typeof window !== "undefined" && window.gdAlertify) {
        return window.gdAlertify;
    }

    if (typeof gdAlertify !== "undefined") {
        return gdAlertify;
    }

    return null;
};

export const getTurnoAuxiliarActualInfo = (row) => {
    if (!row) {
        return {
            id: null,
            nombre: "",
            rut: "",
        };
    }

    if (row.reemplazoNombre && row.auxiliarID != null && row.es_reemplazo == 0) {
        return {
            id: row.reemplazoID || row.auxiliarID || null,
            nombre: row.reemplazoNombre || "",
            rut: row.reemplazoRut || "",
        };
    }

    return {
        id: row.auxiliarID || null,
        nombre: row.auxiliarNombre || "",
        rut: row.auxiliarRut || "",
    };
};

export const getValorTurnoActual = (row = {}) => {
    return Number(
        row.valorTurnoRt ||
        row.reemplazoValorTurno ||
        row.reemplazoValor ||
        row.valorTurno ||
        0
    );
};

export const getValorMovilizacionActual = (row = {}) => {
    if (row.reemplazoValor > 0 && row.es_reemplazo == 0) {
        return Number(row.reemplazoValorMovilizacion ?? row.valorLocomocionRt ?? row.valorMovilizacion ?? 0);
    }

    return Number(
        row.valorLocomocionRt ??
        row.reemplazoValorMovilizacion ??
        row.valorMovilizacion ??
        row.valorLocomocion ??
        0
    );
};

export const getValorColacionActual = (row = {}) => {
    if (row.reemplazoValor > 0 && row.es_reemplazo == 0) {
        return Number(row.reemplazoValorColacion ?? row.valorColacionRt ?? row.valorColacion ?? 0);
    }

    return Number(
        row.valorColacionRt ??
        row.reemplazoValorColacion ??
        row.valorColacion ??
        0
    );
};

export const getDayOfWeek = (dateString) => {
    const days = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
    const date = new Date(dateString);
    return days[date.getDay()];
};

// Mapa de color por nombre de paciente. A diferencia del archivo viejo (donde
// el mapa se re-creaba en cada recálculo de `columns`), aquí vive a nivel de
// módulo para que el color asignado a un paciente sea estable durante toda
// la sesión, no solo dentro de un mismo render de la tabla.
const colorPalette = [
    "#C62828", "#1565C0", "#2E7D32", "#F9A825", "#6A1B9A",
    "#00897B", "#FF8F00", "#4527A0", "#0277BD", "#5D4037",
    "#AD1457", "#283593", "#558B2F", "#D84315", "#303F9F",
    "#FF6F00", "#455A64", "#9E9D24", "#4E342E", "#00838F"
];

const nombreColorMap = {};
let nextColorIndex = 0;

export const getColorForNombre = (nombre) => {
    if (nextColorIndex == 20) {
        nextColorIndex = 0;
    }

    if (!nombreColorMap[nombre]) {
        nombreColorMap[nombre] = colorPalette[nextColorIndex % colorPalette.length];
        nextColorIndex++;
    }
    return nombreColorMap[nombre];
};

export const limpiarNotaDescuentoAuto = (nota = "") => {
    return nota
        .replace(/(^|\s+)Descuento HE:\s*\d+\s*horas(?:\s*-\s*[^.]+)?\./gi, " ")
        .replace(/\s{2,}/g, " ")
        .trim();
};

export const getDescuentoHorasNota = (row = {}) => {
    const horas = parseInt(row.horasDescuentoExtra || 0, 10);
    if (horas <= 0) return "";

    const pagador = row.pagadorDescuentoExtra === "familia" || row.pagadorDescuentoExtra === "paciente"
        ? "Familia"
        : row.pagadorDescuentoExtra === "ambas"
            ? "Ambas"
            : "Auxiliar";
    const concepto = (row.conceptoDescuentoExtra || "").trim();

    return `Descuento HE: ${horas} horas ${pagador}${concepto ? ` - ${concepto}` : ""}`;
};

export const appendNotaCalculada = (nota, texto) => {
    if (!texto) return nota;
    return nota ? `${nota} - ${texto}` : texto;
};

export const NotasText = (row) => {
    let nota = limpiarNotaDescuentoAuto(row.notes || "");

    if (row.reemplazoNombre) {
        nota += ` (Reemplazo) `;
    }

    // Agregar notas internas con separador
    if (row.notas_internas) {
        nota += (nota ? " - " : "") + row.notas_internas;
    }

    if (row.adicionales?.length > 0) {
        row.adicionales.forEach(item => {
            nota += ` - Adicional a ${item.auxiliarNombre} `;

            const addIf = (val, label) => {
                // Solo agrega si es un número diferente de 0
                if (val !== null && val !== undefined && Number(val) !== 0) {
                    nota += ` ${label} $ ${formatMoney(val)}`;
                }
            };

            addIf(item.valorTurno, "turno");
            addIf(item.valorColacion, "colación");
            addIf(item.valorMovilizacion, "movilización");
            addIf(item.valorOtro, "otro");

            if (item.motivoAdicional) {
                nota += ` motivo ${item.motivoAdicional}`;
            }
        });
    }

    nota = appendNotaCalculada(nota, getDescuentoHorasNota(row));

    return nota;
};

// Mes actual, saltando al siguiente si ya estamos desde el día 26 en
// adelante (los periodos de turnos van del 26 al 25).
export const useCustomMonth = () => {
    const today = new Date();
    const day = today.getDate();

    if (day >= 26) {
        today.setDate(1); // Evitar desbordamiento si el próximo mes tiene menos días (ej: 31 de marzo -> 1 de mayo)
        today.setMonth(today.getMonth() + 1);
    }

    return today.toISOString().slice(0, 7);
};

export const limpiarBackdropsHuerfanos = () => {
    setTimeout(() => {
        document
            .querySelectorAll(".gd-modal-backdrop, .modal-backdrop")
            .forEach((element) => {
                if (!document.querySelector(".modal.show, .modal.d-block, .gd-modal")) {
                    element.remove();
                }
            });
        document.body.classList.remove("modal-open");
        document.body.style.removeProperty("overflow");
        document.body.style.removeProperty("padding-right");
    }, 150);
};
